import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { EMPTY, filter, fromEvent, map, merge, Observable } from 'rxjs';
import { deviceActions } from '../actions/device.actions';
import { selectLocked } from '../selectors/device.selector';

// The device store reacts to device-level events. The re-auth saga lives in the bridge.
// No facades — coordinate through the store only.
@Injectable()
export class DeviceEffects {
    private readonly store = inject(Store);
    private readonly document = inject(DOCUMENT);

    // Native: emit whenever the app leaves the foreground (background / screen-off / power button).
    // The Observable owns the listener, so it is removed when the effect subscription tears down.
    private readonly appInactive$ = Capacitor.isNativePlatform()
        ? new Observable<void>((subscriber) => {
              let listener: PluginListenerHandle | undefined;
              let removed = false;
              void App.addListener('appStateChange', ({ isActive }) => {
                  if (!isActive) {
                      subscriber.next(undefined);
                  }
              }).then((handle) => {
                  if (removed) {
                      void handle.remove();
                  } else {
                      listener = handle;
                  }
              });

              return () => {
                  removed = true;
                  void listener?.remove();
              };
          })
        : EMPTY;

    // Lock when the app becomes inactive (Page Visibility on web/WebView, app-state on native).
    lockOnInactive$ = createEffect(() => {
        return merge(fromEvent(this.document, 'visibilitychange').pipe(filter(() => this.document.hidden)), this.appInactive$).pipe(
            concatLatestFrom(() => this.store.select(selectLocked)),
            filter(([, locked]) => !locked),
            map(() => deviceActions.lock()),
        );
    });
}
